import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CSpinner, useColorModes } from '@coreui/react';
import './scss/style.scss';
import PrivateRoute from './components/PrivateRoute';
import useAuth from './hooks/useAuth';
import { setLogUserContext } from './utils/logService';

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'));

// Pages
const Login = React.lazy(() => import('./views/users/Login'));
const Logout = React.lazy(() => import('./views/users/Logout'));
const ResetPassword = React.lazy(() => import('./views/users/ResetPassword'));
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'));
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'));
const DailySummary = React.lazy(() => import('./views/scheduling/DailySummary'));
const ShortUrlRedirect = React.lazy(() => import('./views/shorturl/ShortUrlRedirect'));
const ApproveTicket = React.lazy(() => import('./views/extrawork/ExtraWorkApproval'));
const RejectTicket = React.lazy(() => import('./views/extrawork/ExtraWorkRejection'));
const ExternalSignatureView = React.lazy(() => import('./views/extrawork/ExternalSignatureView'));
const EmploymentApplicationForm = React.lazy(() => import('./views/forms/employmentapplication/EmploymentApplicationForm'));

const AuthRedirectHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleUnauthorizedRedirect = (event) => {
      // event.detail.nextPath is the URL path that caused the 401
      const nextUrl = `/login?next=${encodeURIComponent(event.detail.nextPath)}`;
      
      // Use the router's navigate function for safe internal redirect,
      // resolving the security flag on window.location.href.
      navigate(nextUrl);
    };

    window.addEventListener('unauthorized-redirect', handleUnauthorizedRedirect);

    return () => {
      window.removeEventListener('unauthorized-redirect', handleUnauthorizedRedirect);
    };
  }, [navigate]);

  return null;
};

const App = () => {
  const { user } = useAuth()
  const { isColorModeSet, setColorMode } = useColorModes('coreui-free-react-admin-template-theme');
  const storedTheme = useSelector((state) => state.theme);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.href.split('?')[1]);
    const theme = urlParams.get('theme') && urlParams.get('theme').match(/^[A-Za-z0-9\s]+/)[0];
    if (theme) {
      setColorMode(theme);
    }

    if (isColorModeSet()) {
      return;
    }

    setColorMode(storedTheme);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) {
      setLogUserContext(user) // attach user info to every log
    }
  }, [user])

  return (
    <Router>
      {/* 2. 🛡️ INTEGRATE HANDLER: This runs the logic for the safe redirect */}
      <AuthRedirectHandler />
      
      <Suspense
        fallback={
          <div className="pt-3 text-center">
            <CSpinner color="primary" variant="grow" />
          </div>
        }
      >
        <Routes>
          {/* FIX: Removed duplicate <Route exact path="/login" ... /> */}
          <Route exact path="/" name="Login Page" element={<Login />} />
          <Route exact path="/login" name="Login Page" element={<Login />} />
          <Route exact path="/logout" name="Logout Page" element={<Logout />} />
          <Route exact path="/reset-password" name="Reset Password" element={<ResetPassword />} />
          <Route exact path="/404" name="Page 404" element={<Page404 />} />
          <Route exact path="/500" name="Page 500" element={<Page500 />} />
          <Route exact path="/daily" name="Daily Summary" element={<DailySummary />} />
          <Route exact path="/s/:shortId" name="Short URL" element={<ShortUrlRedirect />} />
          <Route exact path="/approve-ticket/:ticketId" name="Approve Ticket" element={<ApproveTicket />} />
          <Route exact path="/reject-ticket/:ticketId" name="Reject Ticket" element={<RejectTicket />} />
          <Route exact path="/sign/:id" name="Signature" element={<ExternalSignatureView />} />
          <Route exact path="/employment-application" name="Employment Application" element={<EmploymentApplicationForm />} />

          {/* Protect this route with PrivateRoute, catches all other paths */}
          <Route
            path="*"
            name="Home"
            element={
              <PrivateRoute>
                <DefaultLayout />
              </PrivateRoute>
            }
          />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;